import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const MetricCard = ({ title, value, icon, color, subtitle }) => {
  const colorMap = {
    primary: { bg: 'bg-primary/10', text: 'text-primary', icon: 'var(--color-primary)' },
    success: { bg: 'bg-success/10', text: 'text-success', icon: 'var(--color-success)' },
    warning: { bg: 'bg-warning/10', text: 'text-warning', icon: 'var(--color-warning)' },
    error: { bg: 'bg-error/10', text: 'text-error', icon: 'var(--color-error)' },
    secondary: { bg: 'bg-secondary/10', text: 'text-secondary', icon: 'var(--color-secondary)' },
    accent: { bg: 'bg-accent/10', text: 'text-accent', icon: 'var(--color-accent)' },
  };
  const c = colorMap?.[color] || colorMap?.primary;
  return (
    <div className="bg-card rounded-xl border border-border p-5 hover:border-primary/30 transition-all duration-200">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${c?.bg}`}>
          <Icon name={icon} size={22} color={c?.icon} />
        </div>
      </div>
      <p className="text-2xl font-bold text-foreground mb-1">{value}</p>
      <p className="text-sm font-medium text-foreground mb-0.5">{title}</p>
      {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
    </div>
  );
};

const DashboardOverview = ({ dashboardData, loading, onNavigate }) => {
  const [chartData, setChartData] = useState([]);
  const [rewardTrend, setRewardTrend] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [chartsLoading, setChartsLoading] = useState(true);

  const loadCharts = useCallback(async () => {
    try {
      const [txRes, ledgerRes, activityRes] = await Promise.all([
        supabase?.from('transactions')?.select('created_at, status')?.order('created_at', { ascending: true }),
        supabase?.from('wallet_ledger')?.select('created_at, amount, ledger_type')?.eq('ledger_type', 'credit')?.order('created_at', { ascending: true }),
        supabase?.from('moderation_logs')?.select(`id, action, created_at, moderator:user_profiles!moderation_logs_moderator_id_fkey(full_name, email)`)?.order('created_at', { ascending: false })?.limit(6),
      ]);

      // User growth by day of week
      const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const dayMap = {};
      days?.forEach(d => { dayMap[d] = { name: d, submissions: 0, approved: 0 }; });
      txRes?.data?.forEach(t => {
        const d = new Date(t.created_at);
        const key = days?.[(d?.getDay() + 6) % 7];
        if (dayMap?.[key]) {
          dayMap[key].submissions += 1;
          if (t?.status === 'approved') dayMap[key].approved += 1;
        }
      });
      setChartData(days?.map(d => dayMap?.[d]));

      // Reward payout trend by month
      const monthMap = {};
      ledgerRes?.data?.forEach(l => {
        const d = new Date(l.created_at);
        const key = d?.toLocaleString('default', { month: 'short' });
        if (!monthMap?.[key]) monthMap[key] = { name: key, rewards: 0 };
        monthMap[key].rewards += parseFloat(l?.amount || 0);
      });
      setRewardTrend(Object.values(monthMap)?.slice(-6));

      // Recent activity
      const mapped = activityRes?.data?.map(log => ({
        action: log?.action === 'approved' ? 'Approved submission' : log?.action === 'rejected' ? 'Rejected submission' : log?.action === 'flagged' ? 'Flagged submission' : `${log?.action}`,
        user: log?.moderator?.full_name || log?.moderator?.email || 'Admin',
        time: getRelativeTime(new Date(log.created_at)),
        type: log?.action,
      })) || [];
      setRecentActivity(mapped);
    } catch (err) {
      console.error('[DashboardOverview] loadCharts error:', err);
    } finally {
      setChartsLoading(false);
    }
  }, []);

  useEffect(() => { loadCharts(); }, [loadCharts]);

  const getRelativeTime = (date) => {
    const diffMs = Date.now() - date?.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  const metrics = [
    { title: 'Total Users', value: dashboardData?.totalUsers?.toLocaleString(), icon: 'Users', color: 'primary', subtitle: 'All registered accounts' },
    { title: 'Influencers', value: dashboardData?.totalInfluencers?.toLocaleString(), icon: 'Star', color: 'secondary', subtitle: 'Active influencers' },
    { title: 'Brand Partners', value: dashboardData?.totalBrands?.toLocaleString(), icon: 'Building2', color: 'accent', subtitle: 'Registered brands' },
    { title: 'Active Campaigns', value: dashboardData?.activeCampaigns?.toLocaleString(), icon: 'Megaphone', color: 'success', subtitle: 'Currently running' },
    { title: 'Rewards Distributed', value: `$${(dashboardData?.totalRewards || 0)?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`, icon: 'DollarSign', color: 'success', subtitle: 'Total paid out' },
    { title: 'Pending Moderation', value: dashboardData?.pendingModeration?.toLocaleString(), icon: 'Clock', color: 'warning', subtitle: 'Awaiting review' },
  ];

  const actionIconColor = (type) => {
    if (type === 'approved') return { bg: 'bg-success/10', color: 'var(--color-success)', icon: 'CheckCircle' };
    if (type === 'rejected') return { bg: 'bg-error/10', color: 'var(--color-error)', icon: 'XCircle' };
    if (type === 'flagged') return { bg: 'bg-warning/10', color: 'var(--color-warning)', icon: 'Flag' };
    return { bg: 'bg-muted', color: 'var(--color-muted-foreground)', icon: 'Activity' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Metrics grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {metrics?.map((m, i) => <MetricCard key={i} {...m} />)}
      </div>
      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-xl border border-border p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">Campaign Engagement (This Week)</h3>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="name" stroke="var(--color-muted-foreground)" style={{ fontSize: '11px' }} />
                <YAxis stroke="var(--color-muted-foreground)" style={{ fontSize: '11px' }} />
                <Tooltip contentStyle={{ backgroundColor: 'var(--color-popover)', border: '1px solid var(--color-border)', borderRadius: '8px', color: 'var(--color-foreground)', fontSize: '12px' }} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Bar dataKey="submissions" name="Submissions" fill="var(--color-primary)" radius={[3, 3, 0, 0]} />
                <Bar dataKey="approved" name="Approved" fill="var(--color-success)" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">Reward Payout Trends</h3>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={rewardTrend}>
                <defs>
                  <linearGradient id="rewardGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="name" stroke="var(--color-muted-foreground)" style={{ fontSize: '11px' }} />
                <YAxis stroke="var(--color-muted-foreground)" style={{ fontSize: '11px' }} />
                <Tooltip contentStyle={{ backgroundColor: 'var(--color-popover)', border: '1px solid var(--color-border)', borderRadius: '8px', color: 'var(--color-foreground)', fontSize: '12px' }} formatter={(v) => [`$${v?.toFixed(2)}`, 'Rewards']} />
                <Area type="monotone" dataKey="rewards" stroke="var(--color-primary)" fill="url(#rewardGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {/* Activity feed + Quick actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-card rounded-xl border border-border p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">Recent Activity</h3>
          {recentActivity?.length > 0 ? (
            <div className="space-y-3">
              {recentActivity?.map((a, i) => {
                const style = actionIconColor(a?.type);
                return (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/40">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${style?.bg}`}>
                      <Icon name={style?.icon} size={15} color={style?.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{a?.action}</p>
                      <p className="text-xs text-muted-foreground">{a?.user}</p>
                    </div>
                    <span className="text-xs text-muted-foreground flex-shrink-0">{a?.time}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <Icon name="Activity" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No recent activity</p>
            </div>
          )}
        </div>

        <div className="bg-card rounded-xl border border-border p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">Quick Actions</h3>
          <div className="space-y-2">
            {[
              { label: 'Review Pending Submissions', icon: 'ShieldCheck', section: 'moderation', color: 'text-warning' },
              { label: 'Approve New Brands', icon: 'Building2', section: 'brands', color: 'text-primary' },
              { label: 'View User Reports', icon: 'Users', section: 'users', color: 'text-secondary' },
              { label: 'Check Rewards Ledger', icon: 'Wallet', section: 'rewards', color: 'text-success' },
              { label: 'Send Announcement', icon: 'Bell', section: 'notifications', color: 'text-accent' },
              { label: 'View Audit Logs', icon: 'FileSearch', section: 'audit', color: 'text-muted-foreground' },
            ]?.map((action, i) => (
              <button
                key={i}
                onClick={() => onNavigate(action?.section)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-left group"
              >
                <Icon name={action?.icon} size={16} className={action?.color} />
                <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">{action?.label}</span>
                <Icon name="ChevronRight" size={14} className="ml-auto text-muted-foreground" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;
